//src/App.js
import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { onAuthStateChanged, signInWithPopup } from "firebase/auth";
import {
  saveSchedule,
  updateSchedule,
  deleteSchedule,
  getUserSchedules,
  migrateLegacySchedules,
} from "./firebase/scheduleService";
import { CryptoProvider, useCrypto } from "./context/CryptoContext";
import CryptoGate from "./components/CryptoGate";

import LandingPage from "./pages/LandingPage";
import Page0 from "./pages/Page0";
import Page1 from "./pages/Page1";
import Page2 from "./pages/Page2";
import About from "./pages/About";
import Support from "./pages/Support";
import Layout from "./components/Layout";
import { auth, provider } from "./firebase/firebase";
import Navbar from "./components/Navbar";
import { motion, AnimatePresence } from "framer-motion";
import AnalyticsTracker from "./components/AnalyticsTracker";
import { CircularProgress, Box, Typography } from "@mui/material";

function AnimatedRoutes({ user, guest, onGuest, onExitGuest, handleSignOut }) {
  const hasAccess = !!user || guest;
  const location = useLocation();
  const navigate = useNavigate();
  const { dek } = useCrypto();

  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(true);
  const IGNORED_AUTH_ERRORS = [
    "auth/popup-closed-by-user",
    "auth/cancelled-popup-request",
  ];

  const signIn = () =>
    signInWithPopup(auth, provider).catch((err) => {
      if (IGNORED_AUTH_ERRORS.includes(err.code)) return; // user cancelled
      console.error("Sign-in failed:", err.code);
      alert(err.message);
    });

  // Load only after unlock
  useEffect(() => {
    if (user && dek) {
      loadUserSchedules(user.uid);
    } else {
      setSchedules([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dek]);

  const loadUserSchedules = async (userId) => {
    setSchedulesLoading(true);
    try {
      let list = await getUserSchedules(userId, dek);

      // One-time migration of old plaintext docs
      if (list.some((s) => s._legacy)) {
        await migrateLegacySchedules(userId, dek);
        list = await getUserSchedules(userId, dek);
      }

      const valid = list.filter(
        (s) => s.id && typeof s.id === "string" && s.id.length > 10,
      );
      setSchedules(valid);
    } catch (error) {
      console.error("Failed to load schedules:", error);
    } finally {
      setSchedulesLoading(false);
    }
  };

  const handleSaveSchedule = async (scheduleData) => {
    if (!user || !dek) throw new Error("LOCKED");
    try {
      const isUpdate =
        scheduleData.id &&
        typeof scheduleData.id === "string" &&
        scheduleData.id.length > 10;
      const existing = isUpdate
        ? schedules.find((s) => s.id === scheduleData.id)
        : null;

      if (existing) {
        const saved = await updateSchedule(
          scheduleData.id,
          { ...scheduleData, createdAt: existing.createdAt },
          user.uid,
          dek,
        );
        setSchedules((prev) =>
          prev.map((s) => (s.id === saved.id ? saved : s)),
        );
      } else {
        const saved = await saveSchedule(user.uid, scheduleData, dek);
        setSchedules((prev) => [saved, ...prev]);
      }
    } catch (error) {
      console.error("Failed to save schedule:", error);
      throw error;
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!user || !scheduleId || typeof scheduleId !== "string") return;
    try {
      await deleteSchedule(scheduleId);
      setSchedules((prev) => prev.filter((s) => s.id !== scheduleId));
    } catch (error) {
      console.error("Failed to delete schedule:", error);
    }
  };

  const handleDuplicateSchedule = async (duplicatedSchedule) => {
    if (!user || !dek) return;
    try {
      const saved = await saveSchedule(user.uid, duplicatedSchedule, dek);
      setSchedules((prev) => [saved, ...prev]);
    } catch (error) {
      console.error("Failed to duplicate schedule:", error);
    }
  };

  // Guest: set flag AND navigate to setup page
  const handleGuest = () => {
    onGuest();
    navigate("/new");
  };

  // Guest exit: clear flag AND go back to landing
  const handleExitGuest = () => {
    onExitGuest();
    navigate("/");
  };

  const pageVariants = {
    initial: { opacity: 0, x: 100 },
    in: { opacity: 1, x: 0 },
    out: { opacity: 0, x: -100 },
  };

  const pageTransition = {
    type: "tween",
    ease: "anticipate",
    duration: 0.5,
  };

  // Logged in but locked: show nothing sensitive
  if (user && !dek) {
    return <Navbar user={user} onSignOut={handleSignOut} />;
  }

  const handleSignIn = () => signInWithPopup(auth, provider).catch(alert);

  return (
    <>
      <Navbar
        user={user}
        guest={guest}
        onSignOut={user ? handleSignOut : handleExitGuest}
        onSignIn={handleSignIn}
      />
      <AnalyticsTracker />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route
            path="/"
            element={
              user ? (
                <Navigate to="/home" />
              ) : (
                <LandingPage onSignIn={signIn} onGuest={handleGuest} />
              )
            }
          />
          <Route
            path="/home"
            element={
              user ? (
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <Layout onSignOut={handleSignOut}>
                    <Page0
                      user={user}
                      schedules={schedules}
                      onDeleteSchedule={handleDeleteSchedule}
                      onDuplicateSchedule={handleDuplicateSchedule}
                      schedulesLoading={schedulesLoading}
                    />
                  </Layout>
                </motion.div>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/new"
            element={
              hasAccess ? (
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <Layout onSignOut={handleSignOut}>
                    <Page1 />
                  </Layout>
                </motion.div>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/page2"
            element={
              hasAccess ? (
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <Layout onSignOut={handleSignOut}>
                    <Page2
                      onSaveSchedule={handleSaveSchedule}
                      userSchedules={schedules}
                      isGuest={!user}
                    />
                  </Layout>
                </motion.div>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/edit"
            element={
              hasAccess ? (
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                >
                  <Layout onSignOut={handleSignOut}>
                    <Page2
                      onSaveSchedule={handleSaveSchedule}
                      userSchedules={schedules}
                      isGuest={!user}
                    />
                  </Layout>
                </motion.div>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/about"
            element={
              <Layout onSignOut={handleSignOut}>
                <About />
              </Layout>
            }
          />
          <Route
            path="/support"
            element={
              <Layout onSignOut={handleSignOut}>
                <Support />
              </Layout>
            }
          />
          <Route
            path="*"
            element={
              <Layout onSignOut={handleSignOut}>
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                  className="flex flex-col items-center justify-center h-[70vh] text-center"
                >
                  <br />
                  <Typography variant="h3" color="text.primary" gutterBottom>
                    404 - Page Not Found
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    The page you're looking for doesn't exist.
                  </Typography>
                </motion.div>
              </Layout>
            }
          />
        </Routes>
      </AnimatePresence>
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guest, setGuest] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) setGuest(false);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const handleSignOut = () => {
    auth.signOut().catch((err) => {
      console.error("Sign-out failed:", err.code);
      alert(err.message);
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          gap: 2,
        }}
      >
        <CircularProgress color="secondary" size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Loading, please wait...
        </Typography>
      </Box>
    );
  }

  return (
    <CryptoProvider user={user}>
      <Router>
        <AnimatedRoutes
          user={user}
          guest={guest}
          onGuest={() => setGuest(true)}
          onExitGuest={() => setGuest(false)}
          handleSignOut={handleSignOut}
        />
        {user && <CryptoGate onSignOut={handleSignOut} />}
      </Router>
    </CryptoProvider>
  );
}

export default App;
